import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import apiClient from "@/api-client.js";
import { useAuth } from "@/components/contexts/AuthContext.jsx";
import { CATALOG_STALE_TIME, getRankTrackingCountries, rankTrackingKeys } from "@/services/rankTrackingService.js";
import { getSeRankingAvailableSites } from "@/services/seRankingService.js";
import { emptyDraft } from "./seoSetupUtils.js";

export function useSeoSetupClient(clientId, { enabled = true } = {}) {
  const { interceptorReady } = useAuth();
  return useQuery({
    queryKey: ["seo-setup", "client", Number(clientId)],
    queryFn: async () => (await apiClient.get(`clients/${clientId}/`)).data,
    enabled: enabled && interceptorReady && Boolean(clientId),
  });
}

/** The requester's sub-account projects for this client; `connected: false` when they have none. */
export function useSeRankingAvailableSites(clientId, { enabled = true } = {}) {
  const { interceptorReady } = useAuth();
  return useQuery({
    queryKey: ["seranking", "available-sites", Number(clientId)],
    queryFn: () => getSeRankingAvailableSites(clientId),
    enabled: enabled && interceptorReady && Boolean(clientId),
    // Live SE Ranking call: shared by SeoConnectModal and the picker it opens.
    staleTime: 60 * 1000,
  });
}

// A blocked localStorage just means the stepper cannot be resumed.
const SEO_SETUP_EVENT = "mkz:seo-setup-change";
const storageKey = (clientId) => `mkz:seo-setup:${clientId}`;

const readSeoSetupStorage = (clientId) => {
  if (!clientId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(clientId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** `null` removes the key. */
const writeSeoSetupStorage = (clientId, value) => {
  if (!clientId) return;
  try {
    if (value == null) window.localStorage.removeItem(storageKey(clientId));
    else window.localStorage.setItem(storageKey(clientId), JSON.stringify(value));
  } catch {
    // Storage blocked: the draft only lives in memory.
  }
  // `storage` events only reach other tabs; this one tells this tab's rows.
  try {
    window.dispatchEvent(new CustomEvent(SEO_SETUP_EVENT, { detail: { clientId: String(clientId) } }));
  } catch {
    // No CustomEvent: badges refresh on the next render.
  }
};

/** `{ status }`: "pending" when there is a draft to resume, else null. */
export function useSeoSetupStorageStatus(clientId) {
  const read = useCallback(() => {
    const stored = readSeoSetupStorage(clientId);
    return { status: stored?.general || stored?.engines ? "pending" : null };
  }, [clientId]);
  const [state, setState] = useState(read);

  useEffect(() => {
    setState(read());
    const refresh = (event) => {
      if (event.type === "storage" && event.key && event.key !== storageKey(clientId)) return;
      if (event.type === SEO_SETUP_EVENT && event.detail?.clientId !== String(clientId)) return;
      setState(read());
    };
    window.addEventListener(SEO_SETUP_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SEO_SETUP_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [clientId, read]);

  return state;
}

const restore = (clientId) => {
  const stored = readSeoSetupStorage(clientId);
  return stored ? { ...emptyDraft(), ...stored } : null;
};

/** Nothing is written until the user changes something (or calls `persist`), so just opening leaves no badge. */
export function useSeoSetupDraft(clientId) {
  const [draft, setDraft] = useState(() => restore(clientId) || emptyDraft());
  const [restored, setRestored] = useState(() => Boolean(restore(clientId)));
  const dirtyRef = useRef(false);

  useEffect(() => {
    const stored = restore(clientId);
    setDraft(stored || emptyDraft());
    setRestored(Boolean(stored));
    dirtyRef.current = false;
  }, [clientId]);

  useEffect(() => {
    if (dirtyRef.current) writeSeoSetupStorage(clientId, { ...draft, updated_at: Date.now() });
  }, [clientId, draft]);

  const merge = useCallback((patch, dirty) => {
    if (dirty) dirtyRef.current = true;
    setDraft((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }));
  }, []);

  const update = useCallback((patch) => merge(patch, true), [merge]);
  // Prefills (client name and website) that do not count as a change.
  const applyDefaults = useCallback((patch) => merge(patch, false), [merge]);
  const persist = useCallback(() => merge({}, true), [merge]);

  const clearStorage = useCallback(() => {
    dirtyRef.current = false;
    writeSeoSetupStorage(clientId, null);
  }, [clientId]);

  const replace = useCallback((next) => {
    dirtyRef.current = true;
    setDraft({ ...emptyDraft(), ...next });
  }, []);

  return { draft, update, applyDefaults, persist, clearStorage, replace, restored };
}

/** From this browser's storage only: "pending" when a setup was left halfway. */
export function useSeoSetupRow(clientId, { enabled, linked }) {
  const { status } = useSeoSetupStorageStatus(clientId);
  return enabled && !linked ? status : null;
}

/** Status text that clears itself after `ms`. */
export function useFlashNotice(ms) {
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!notice) return undefined;
    const timeout = setTimeout(() => setNotice(""), ms);
    return () => clearTimeout(timeout);
  }, [notice, ms]);
  return [notice, setNotice];
}

export function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
}

/** `{ ES: "España", … }` from the cached country catalogue. */
export function useCountryNames() {
  const { data } = useQuery({
    queryKey: rankTrackingKeys.countries(),
    queryFn: getRankTrackingCountries,
    staleTime: CATALOG_STALE_TIME,
  });
  return Object.fromEntries((data || []).map((country) => [country.code, country.name]));
}
